import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from "react-router-dom";
import {IconArrowLeft, IconUsers, IconEdit, IconPlus, IconInfoCircle, IconTrashFilled} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import LoadinPage from "../../Components/LoadingPage";
import NewAthleteModal from "./Modals/NewAthlete";
import {getEventById, getAthletesByEvent, getAthleteById, deleteAthlete, deleteEvent} from "../../db";

const Detail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newAthleteModal, setNewAthleteModal] = useState(false);
    const [eventAthletes, setEventAthletes] = useState([]);

    useEffect(() => {
        const fetchEvent = async () => {
            const foundEvent = await getEventById(id);
            setLoading(false);

            if (foundEvent) {
                setEvent(foundEvent);
                fetchEventAthletes(foundEvent.id); // ✅ Fetch linked athletes after loading event
            } else {
                navigate("/events", { state: { error: t("eventDetail.notFound") } });
            }
        };
        fetchEvent();
    }, [id, navigate]);

    const fetchEventAthletes = async (eventId) => {
        const athletes = await getAthletesByEvent(eventId); // now returns full records
        const sorted = athletes
            .filter(Boolean)
            .sort((a, b) => {
                const aBib = a.eventInfo?.bib;
                const bBib = b.eventInfo?.bib;

                if (!aBib && !bBib) return 0;         // both missing
                if (!aBib) return -1;                 // a has no bib, b does
                if (!bBib) return 1;                  // b has no bib, a does
                return Number(aBib) - Number(bBib);   // both have bibs, sort numerically
            });
        setEventAthletes(sorted);
    };

    const handleModalClose = () => {
        setNewAthleteModal(false);
        fetchEventAthletes(event.id); // ✅ Refresh athlete list after modal closes
    };

    const handleDelete = async (id) => {
        if (window.confirm(t("event.removeConfirmation"))) {
            await deleteEvent(id);
            navigate("/events", { state: { error: t("athletesDetail.removed") } });
        }
    };

    if (loading) return (
        <div className="p-4 flex flex-col h-full relative">
            <div className="flex items-center pb-4 w-full">
                <div className="shrink-0 w-10">
                    <Link to="/events">
                        <IconArrowLeft className="w-6 h-auto" />
                        <span className="sr-only">{t("eventDetail.backToList")}</span>
                    </Link>
                </div>
                <div className="grow text-2xl uppercase text-center font-black transform -skew-x-12 shrink-0">
                </div>
                <div className="shrink-0 w-10">
                </div>
            </div>

            <div className="grow overflow-y-auto overflow-x-hidden">
                <LoadinPage />
            </div>
        </div>
    );

    return (
        <div className="p-4 flex flex-col h-full relative">
            <div className="flex items-center pb-4 w-full">
                <div className="shrink-0 w-10">
                    <Link to="/events">
                        <IconArrowLeft className="w-6 h-auto" />
                        <span className="sr-only">{t("eventDetail.backToList")}</span>
                    </Link>
                </div>
                <div className="grow text-2xl uppercase text-center font-black transform -skew-x-12 shrink-0">
                    {event.title}
                </div>
                <div className="shrink-0 w-10">
                    <Link to={`/events/${event.id}/edit`}>
                        <IconEdit className="w-6 h-auto" />
                        <span className="sr-only">{t("eventDetail.edit")}</span>
                    </Link>
                </div>
            </div>

            <div className="grow overflow-y-auto overflow-x-hidden flex flex-col gap-6">
                <section className="flex flex-col gap-4" data-type="Info">
                    <h2 className="text-lg uppercase font-black transform -skew-x-12 border-b-2 border-[var(--light-color)] flex items-center gap-2">
                        <IconInfoCircle className="w-6 h-auto transform skew-x-12" />
                        <span className="grow">{t("eventDetail.generalInfo")}</span>
                    </h2>
                    <div>
                        <table className="simple-table">
                            <tbody>
                                {event.date && (
                                    <tr>
                                        <th>{t("event.date")}</th>
                                        <td>{new Intl.DateTimeFormat("cs-CZ", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric"
                                        }).format(new Date(event.date * 1000))}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
                <section className="flex flex-col gap-4" data-type="Athletes">
                    <h2 className="text-lg uppercase font-black transform -skew-x-12 border-b-2 border-[var(--light-color)] flex items-center gap-2">
                        <IconUsers className="w-6 h-auto transform skew-x-12" />
                        <span className="grow">{t("eventDetail.athletes")}</span>
                    </h2>
                    <div>
                        <div className="flex flex-col mb-6 sm:flex-row gap-2 px-2">
                            <button onClick={() => setNewAthleteModal(true)} className="button-primary text-xs">
                                <IconPlus className="w-4 h-auto transform skew-x-12" />
                                {t("eventDetail.athletes.addNew")}
                            </button>
                            {newAthleteModal && (
                                <NewAthleteModal onClose={() => handleModalClose()}
                                                 eventId={event.id}
                                />
                            )}
                        </div>

                        {eventAthletes.length > 0 ? (
                            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 text-xs px-2">
                                {eventAthletes.map((athlete) => (
                                    <Link key={athlete.id} to={`/events/${event.id}/athlete/${athlete.id}`} className="button-secondary flex-col !items-start justify-between">
                                        <div className="flex gap-1 items-center">
                                            {athlete.eventInfo.bib && (
                                                <div className="aspect-[4/3] w-8 items-center justify-center inline-flex p-1 text-xs bg-[var(--light-color)] text-[var(--dark-color)]">
                                                    {athlete.eventInfo.bib}
                                                </div>
                                            )}
                                            <span>{athlete.nickname || `${athlete.firstname} ${athlete.lastname}`}</span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 px-2">{t("eventDetail.athletes.noLinked")}</p>
                        )}
                    </div>
                </section>
            </div>
            <div className="mt-20 px-2">
                <button className="button-secondary" onClick={() => handleDelete(event.id)}>
                    <IconTrashFilled className="w-4 h-auto transform skew-x-12" />
                    {t("eventDetail.remove")}
                </button>
            </div>
        </div>
    );
};

export default Detail;